import { useMemo } from 'react';
import { useCustomFieldSchema } from '@/hooks/custom-fields/useCustomFieldSchema';
import { useCustomFieldValues } from '@/hooks/custom-fields/useCustomFieldValues';
import { useUpsertValues } from '@/hooks/custom-fields/useUpsertValues';
import { useEstimateTemplate } from '@/hooks/estimateTemplates/useEstimateTemplate';
import { useCreateEstimate } from '@/hooks/estimates/useCreateEstimate';
import { useEstimate } from '@/hooks/estimates/useEstimate';
import { useEstimateCustomFieldState } from '@/hooks/estimates/useEstimateCustomFieldState';
import { useSendEstimate } from '@/hooks/estimates/useSendEstimate';
import { useUpdateEstimate } from '@/hooks/estimates/useUpdateEstimate';

interface UseEstimateFormResourcesArgs {
  isEditMode: boolean;
  editId?: string;
  duplicateId?: string;
  selectedTemplateId: string;
}

export function useEstimateFormResources({
  isEditMode,
  editId,
  duplicateId,
  selectedTemplateId,
}: UseEstimateFormResourcesArgs) {
  const createEstimate = useCreateEstimate();
  const updateEstimate = useUpdateEstimate();
  const sendEstimate = useSendEstimate();
  const upsertValues = useUpsertValues();
  const { data: estimateSchema } = useCustomFieldSchema('estimate');
  const estimateCustomFields = useMemo(
    () =>
      (Array.isArray(estimateSchema) ? estimateSchema : []).filter(
        (s) => s.definition?.isActive && s.association?.isActive,
      ),
    [estimateSchema],
  );
  const { data: existingCfValues } = useCustomFieldValues('estimate', editId ?? '');
  const {
    customFields,
    activeFieldKeys,
    setCustomField,
    setActiveFieldKeys,
    removeField,
    applyInheritedDraftValues,
  } = useEstimateCustomFieldState({
    isEditMode,
    estimateCustomFields,
    existingCfValues,
  });
  const { data: existingEstimate, isLoading: loadingEstimate } = useEstimate(editId ?? '');
  const { data: duplicateEstimate, isLoading: loadingDuplicate } = useEstimate(duplicateId ?? '');
  const { data: selectedTemplate } = useEstimateTemplate(selectedTemplateId);

  const submitUpdateEstimate = useMemo(
    () => ({
      isPending: updateEstimate.isPending,
      mutate: (
        nextPayload: unknown,
        options: { onSuccess: (data: unknown) => void; onError?: (error: unknown) => void },
      ) =>
        updateEstimate.mutate(
          nextPayload as Parameters<typeof updateEstimate.mutate>[0],
          options as Parameters<typeof updateEstimate.mutate>[1],
        ),
    }),
    [updateEstimate],
  );

  return {
    createEstimate,
    submitUpdateEstimate,
    sendEstimate,
    upsertValues,
    estimateCustomFields,
    customFields,
    activeFieldKeys,
    setCustomField,
    setActiveFieldKeys,
    removeField,
    applyInheritedDraftValues,
    existingEstimate,
    loadingEstimate,
    duplicateEstimate,
    loadingDuplicate,
    selectedTemplate,
  };
}
